import { z } from "zod";
import { batchCalculationRequestSchema, calculationItemInputSchema } from "./calculation.js";
import { artifactRefSchema, environmentSchema } from "./context.js";
import { lotRecalculationRequestSchema } from "./fiscal-tasks.js";
import { lotStoredImportResponseSchema } from "./lot-import.js";
import { LOT_MAX_DATA_ROWS, lotRouteSchema, lotStoredMappingSchema, researchMissingSchema, } from "./lot-mapping.js";
import { LOT_MAX_REPAIRS, lotStoredRepairRecordSchema } from "./lot-repair.js";
export const lotUploadSchema = lotStoredImportResponseSchema
    .extend({
    workflowId: z.literal("tax-platform-lot-playground"),
    environment: environmentSchema,
    idempotencyKey: z.string().min(1).max(200),
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    createdBy: z.string().uuid(),
    filename: z.string().min(1).max(200),
    status: z.enum(["READY", "INVALID"]),
    artifact: artifactRefSchema,
    createdAt: z.string().datetime(),
    requestId: z.string(),
    correlationId: z.string(),
    workbookId: z.string().uuid().optional(),
    mapping: lotStoredMappingSchema.optional(),
    /** The route the person declared for the file. Absent on uploads made before it existed. */
    route: lotRouteSchema.optional(),
    /** Whether missing coverage may be researched. Stored for the research worker. */
    researchMissing: researchMissingSchema.optional(),
    /**
     * Every repair the person confirmed before the import: the cell, the original and the new value,
     * who confirmed it and when. Absent when nothing was repaired.
     */
    repairs: z.array(lotStoredRepairRecordSchema).max(LOT_MAX_REPAIRS).optional(),
    parserVersion: z.literal("tax-lot-v1"),
})
    .strict();
const uploadedLotItemsSchema = z.array(calculationItemInputSchema).min(1).max(LOT_MAX_DATA_ROWS);
function uniqueRows(input, ctx) {
    for (const field of ["itemId", "position"]) {
        if (new Set(input.items.map((item) => item[field])).size !== input.items.length) {
            ctx.addIssue({ code: "custom", path: ["items"], message: `${field} must be unique` });
        }
    }
}
/**
 * The valid rows of an upload, sent back to create its lot task. A sheet holds up to
 * LOT_MAX_DATA_ROWS rows and a fiscal task runs them in batches of 500, so this request is not
 * bound by the 500 items of the synchronous simulation it used to share a schema with.
 */
export const uploadedLotTaskRequestSchema = z
    .object({
    calculationDate: batchCalculationRequestSchema.shape.calculationDate,
    rulesetSelector: batchCalculationRequestSchema.shape.rulesetSelector,
    items: uploadedLotItemsSchema,
    /** Present when the rows are a recalculation of an earlier task of the same upload. */
    recalculation: lotRecalculationRequestSchema.optional(),
    /**
     * "Calcular N abas": the first calculation of an upload imported and never calculated. When the
     * upload already has a task (a double click, a second tab, a colleague), the API answers that
     * task with 200 instead of creating another one, whatever the Idempotency-Key.
     */
    firstCalculation: z.literal(true).optional(),
})
    .strict()
    .superRefine(uniqueRows)
    .superRefine((input, ctx) => {
    if (input.firstCalculation && input.recalculation)
        ctx.addIssue({
            code: "custom",
            path: ["firstCalculation"],
            message: "A first calculation cannot also be a recalculation",
        });
});
/** The original inputs of an uploaded lot, recovered from its task to recalculate it. */
export const uploadedLotTaskInputSchema = z
    .object({
    calculationDate: batchCalculationRequestSchema.shape.calculationDate,
    items: uploadedLotItemsSchema,
})
    .strict()
    .superRefine(uniqueRows);
//# sourceMappingURL=lot-upload.js.map